import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FaSpinner } from 'react-icons/fa';
import { BtnComponent } from '../../components/button';
import { PageDialogContent, PageDialogFooter } from '../../components/dialog';
import { AdvanceSelect } from '../../components/reactHookFormFields/advanceSelectField';
import { DatePickerFeild } from '../../components/reactHookFormFields/datePicker';
import { useGroupChatContext } from '../../context/groupchatContext';
import { calculateAge } from '../../helpers/utility';
import { useGetPatientAppointmentQuery } from '../../services/appointmentService';
import { chatApi, useGetNursingHomeLocationsQuery } from '../../services/chatService';
import { getBaseUrl, imageFromUuid } from '../../helpers/utility';
import { PatientSearchField } from '../../components/reactHookFormFields/patientSearchField';
import { AsyncProviderSearchField } from '../../components/reactHookFormFields/providerSearchField';
import { HiOutlinePencilAlt } from "react-icons/hi";
import { OptimalCarePracticeID } from '../../utils/constant';
import { TableListing } from '../../components/AppListing/TableListing';

const APPOINTMENTS_PER_PAGE = 20;

const LocationAppointment = ({ closeLocationApptPopup, pid, isOpenApptPopup }) => {
    const {
        practice: { id: practice_id, is_nursing: isNursingPractice },
        auth: authToken,
    } = JSON.parse(localStorage.getItem("auth"));

    const groupChatContext = useGroupChatContext();

    const { authData, selectedChat, setOpenAppt } = useMemo(() => ({
        authData: groupChatContext.authData,
        selectedChat: groupChatContext.selectedChat,
        setOpenAppt: groupChatContext.setOpenAppt,
    }));

    const { control, setValue, reset, handleSubmit, formState: { errors }, } = useForm({
        defaultValues: {
            from_date: moment(),
            to_date: moment().add(1, 'week'),
        },
    });

    const locationVal = useWatch({ control: control, name: "practice_location" });
    const fromDateVal = useWatch({ control: control, name: "from_date" });
    const toDateVal = useWatch({ control: control, name: "to_date" });
    const patienVal = useWatch({ control: control, name: "patient" });
    const providerVal = useWatch({ control: control, name: "provider" });
    const [currentPage, setCurrentPage] = useState(1);

    const payload = {
        location_uuid: pid ? selectedChat?.location?.uuid : locationVal?.map((location) => location.uuid)?.join(','),
        start_date: fromDateVal ? moment(fromDateVal).format('YYYY-MM-DD') : '',
        end_date: toDateVal ? moment(toDateVal).format('YYYY-MM-DD') : '',
        pid: patienVal ? patienVal.pid : pid ? pid : '',
        provider_id: providerVal ? providerVal?.map((provider) => provider.id)?.join(',') : '',
        page: currentPage,
        limit: APPOINTMENTS_PER_PAGE,
    }
    const [isSlotsLoading, setIsSlotsLoading] = useState(false);

    const { data: appointmentsResponse, isLoading, isFetching } = useGetPatientAppointmentQuery(payload, { refetchOnMountOrArgChange: true });
    const appointments = appointmentsResponse?.data || [];
    const appointmentsMeta = appointmentsResponse?.meta || {};
    const totalAppointments = Number(appointmentsMeta?.count || 0);
    const totalPages = Number(appointmentsMeta?.pageCount || Math.ceil(totalAppointments / APPOINTMENTS_PER_PAGE) || 1);
    const { data: nursingHomeLocations } = useGetNursingHomeLocationsQuery({ skip: authData?.is_provider });
    const [getPatientUpcomingAppointments] = chatApi.endpoints.getPatientUpcomingAppointments.useLazyQuery();


    const resetFilters = () => {
        setCurrentPage(1);
        reset();
    }

    const printPayload = {
        duration_filter: "other",
        last_appointment_only: false,
        end_date: toDateVal ? moment(toDateVal).format('YYYY-MM-DD') : '',
        date: fromDateVal ? moment(fromDateVal).format('YYYY-MM-DD') : moment().format("YYYY-MM-DD"),
        actor_id: authData.id,
        location_id: isOpenApptPopup ? locationVal?.map((location) => location.id)?.join(',') : selectedChat?.location?.id,
        pid: patienVal ? patienVal.pid : !isOpenApptPopup ? selectedChat?.pid : '',
        provider_id: providerVal ? providerVal?.map((provider) => provider.id)?.join(',') : '',

    }
    const printAppointment = () => {
        if (!fromDateVal || !toDateVal) {
            return;
        }
        setIsSlotsLoading(true);
        // if(authData?.is_provider){
        //     printPayload.pid = selectedChat?.pid;
        // }
        // else{
        //     printPayload.pid = ''
        // }
        getPatientUpcomingAppointments(printPayload).then((res) => {

            let url = imageFromUuid(res.data.file_uuid);
            window.open(url, "_blank");

            setIsSlotsLoading(false);
        });
    }

    const tableCols = useMemo(() => {
        const cols = [
            {
                title: 'Patient',
                key: 'patient_name',
                colClass: 'flex flex-col text-left',
                getData: (item) => (
                    <>
                        <span className="block font-semibold text-black">{item.patient_name}</span>
                        <span className="text-gray-600"><span className="font-medium">MRN:</span> {item.patient_mrn}</span>
                        <span className="block capitalize text-gray-600">{item.patient_sex}, {moment(item.patient_dob).format('MM/DD/YYYY')} ({calculateAge(item.patient_dob)}y)</span>
                    </>
                ),
            },
            { title: 'Location', key: 'location_name', colClass: 'text-left align-top' },
            {
                title: 'Start Time / Visit Type',
                key: 'visit_type_title',
                colClass: 'flex flex-col text-left align-top',
                getData: (item) => (
                    <>
                        <span>{item.visit_type_title}</span>
                        {item.start_time && <span className="block text-gray-600">{moment(item.start_time).format('hh:mm A')}</span>}
                    </>
                ),
            },
            { title: 'Provider', key: 'provider_name', colClass: 'text-left align-top' },
        ];
        if (OptimalCarePracticeID !== practice_id) {
            cols.push({ title: 'Status', key: 'status', colClass: 'text-left align-top' });
        }
        cols.push({ title: 'Comments', key: 'comments', colClass: 'text-left align-top' });
        if (authData?.is_provider) {
            cols.push({
                title: 'Edit',
                key: 'edit',
                colClass: 'flex justify-center align-top',
                align: 'center',
                skipColHtmlTooltip: true,
                getData: (item) => (
                    <button
                        type="button"
                        onClick={() => setOpenAppt({ isOpen: true, id: item.id })}
                        className="inline-flex items-center justify-center p-1 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                        title="Edit Appointment"
                        aria-label="Edit Appointment"
                    >
                        <HiOutlinePencilAlt className="h-5 w-5 cursor-pointer" />
                    </button>
                ),
            });
        }
        return cols;
    }, [isNursingPractice, practice_id, authData?.is_provider, setOpenAppt]);

    const groupedListData = useMemo(() => {
        if (!appointments?.length) return [];
        return appointments.map((item) => ({
            sectionLabel: moment(item.date).format('dddd, MMM D, YYYY'),
            data: item.data || [],
        }));
    }, [appointments]);

    return (
        <>
            <PageDialogContent
                contentCls="pt-2 pt-2 px-5"
                hideOverFlow={true}
                contentScroll={false}>
                <div className="w-full text-gray-500 min-h-32">
                    {isFetching ?
                        <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto absolute inset-0 outline-none focus:outline-none before:absolute before:flex before:w-full before:h-full z-[999]">
                            <FaSpinner className="animate-spin text-green-300-500 ml-1" size={40} />
                        </div>
                        : null
                    }
                    <div className='pt-4 pb-2 sticky top-[-10px] bg-white z-30'>
                        <div className='grid gap-4 grid-cols-2 md:grid-cols-6 pb-3'>
                            {!pid ?
                                <div className="text-black">
                                    <AdvanceSelect
                                        title="Location"
                                        control={control}
                                        setValue={setValue}
                                        name="practice_location"
                                        optionData={nursingHomeLocations}
                                        showLabel="name"
                                        onChangeHandler={() => setCurrentPage(1)}
                                    />
                                </div>
                                : null}
                            <div className=''>
                                <PatientSearchField
                                    title="Patient Name"
                                    name="patient"
                                    control={control}
                                    onChange={() => setCurrentPage(1)}
                                />
                            </div>
                            <div className=''>
                                <AsyncProviderSearchField
                                    title="Provider Name"
                                    name="provider"
                                    control={control}
                                    isMulti={true}
                                    providerType={!authData?.is_provider ? 'nurse_provider' : ''}
                                    onChange={() => setCurrentPage(1)}
                                />
                            </div>
                            <DatePickerFeild
                                title="From Date"
                                control={control}
                                name="from_date"
                                dateTime={false}
                                rules={{
                                    required: { value: true },
                                }}
                                onChange={() => setCurrentPage(1)}
                                // rules={{
                                //     validate: (value) => {
                                //         if(value && toDateVal && value > toDateVal){
                                //             console.log(value, "valuuu");
                                //             return "From date must be less than or equal to To date"
                                //         }
                                //     },
                                // }}
                                errors={errors}
                            />
                            <DatePickerFeild
                                title="To Date"
                                control={control}
                                name="to_date"
                                dateTime={false}
                                rules={{
                                    required: { value: true },
                                }}
                                onChange={() => setCurrentPage(1)}
                                // defaultValue={new Date()}
                                // rules={{
                                //     validate: (value) => {
                                //         if(value){
                                //             console.log(value, "valuuu");
                                //         }
                                //     },
                                // }}
                                errors={errors}

                            />
                            <div>
                                <BtnComponent text="Reset" btnType="default" onClick={resetFilters} />
                            </div>
                        </div>
                    </div>

                    {(appointments?.length > 0 || isFetching) ? (
                        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 max-h-[calc(100vh_-_300px)]">
                            <TableListing
                                groupedListData={groupedListData}
                                cols={tableCols}
                                loading={isFetching}
                                sectionRowClassName="bg-gray-100 border-b border-gray-200"
                                sectionCellClassName="py-2 px-3 text-sm text-left font-bold text-gray-800"
                            />
                        </div>
                    ) : (
                        <div className="mt-2 py-8 text-center text-gray-500 text-sm rounded-lg border border-gray-200 bg-gray-50/50">No appointment scheduled</div>
                    )}
                    {!isFetching && totalPages > 1 ? (
                        <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 text-sm">
                            <p className="text-gray-700">
                                Page <span className="font-medium">{currentPage}</span> of{" "}
                                <span className="font-medium">{totalPages}</span>
                            </p>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    className="rounded-md border border-gray-300 px-3 py-1.5 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                                    disabled={currentPage <= 1}
                                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    className="rounded-md border border-gray-300 px-3 py-1.5 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </PageDialogContent>
            <PageDialogFooter>
                <BtnComponent loading={isSlotsLoading} text="Print" type="submit" onClick={handleSubmit(printAppointment)} />
                <BtnComponent text="Close" btnType="default" onClick={closeLocationApptPopup} />
            </PageDialogFooter>
        </>
    );
};

export default LocationAppointment;